import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Send, MessageSquare, Flag, ShieldAlert, Loader2 } from "lucide-react";

export default function Messages() {
  const { t } = useTranslation();
  const { otherId } = useParams();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [text, setText] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const endRef = useRef(null);

  const loadThreads = async () => {
    const r = await api.get("/messages/threads");
    setThreads(r.data);
  };

  const loadThread = async (id) => {
    const r = await api.get(`/messages/thread/${id}`);
    setMessages(r.data);
    try {
      const u = await api.get(`/candidates/${id}`).catch(() => api.get(`/employers/${id}`));
      setOtherUser(u.data.user);
    } catch {}
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (otherId) loadThread(otherId); }, [otherId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !otherId) return;
    const r = await api.post("/messages", { recipient_id: otherId, content: text });
    setMessages((m) => [...m, r.data]);
    setText("");
    loadThreads();
  };

  const submitReport = async () => {
    if (!otherId) return;
    setReporting(true);
    try {
      await api.post("/messages/report-fraud", {
        other_user_id: otherId,
        reason: reportReason.trim(),
      });
      toast.success(t("fraud_report.success"));
      setReportOpen(false);
      setReportReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("fraud_report.error"));
    } finally {
      setReporting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="messages-page">
      <div className="mec-overline mb-2">{t("messages.overline")}</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">{t("messages.title")}</h1>

      <div className="grid md:grid-cols-12 gap-4 min-h-[60vh]">
        <div className="md:col-span-4 mec-card p-2 max-h-[70vh] overflow-y-auto">
          {threads.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">{t("messages.no_threads")}</div>
          )}
          {threads.map((th) => (
            <Link key={th.thread_id} to={`/messages/${th.other_user_id}`} className={`block p-3 rounded-md mb-1 hover:bg-slate-50 ${otherId === th.other_user_id ? "bg-blue-50" : ""}`} data-testid={`thread-${th.other_user_id}`}>
              <div className="font-semibold text-sm text-slate-900">{th.other_user_name}</div>
              <div className="text-xs text-slate-500 truncate mt-0.5">{th.last_message}</div>
            </Link>
          ))}
        </div>

        <div className="md:col-span-8 mec-card flex flex-col max-h-[70vh]">
          {!otherId ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                {t("messages.select_thread")}
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                <Link to="/messages" className="md:hidden"><ArrowLeft className="w-4 h-4" /></Link>
                <div className="font-semibold text-slate-900 flex-1">{otherUser?.full_name || t("messages.title")}</div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReportOpen(true)}
                  className="text-rose-600 hover:bg-rose-50 text-xs"
                  data-testid="report-fraud-btn"
                >
                  <Flag className="w-3.5 h-3.5 mr-1" /> {t("fraud_report.button")}
                </Button>
              </div>

              {/* Permanent fraud warning banner inside conversation */}
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid="fraud-warning-strip">
                <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{t("fraud_report.warning_strip")}</span>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-[#004EEB] text-white" : "bg-slate-100 text-slate-900"}`}>
                        {m.content}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("messages.type_message")} data-testid="msg-input" />
                <Button type="submit" className="bg-[#004EEB] hover:bg-[#003BCC]" data-testid="msg-send"><Send className="w-4 h-4" /></Button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Report fraud dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Flag className="w-5 h-5" /> {t("fraud_report.dialog_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 leading-relaxed">{t("fraud_report.dialog_intro")}</p>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
              {t("fraud_report.dialog_examples")}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">{t("fraud_report.reason_label")}</label>
              <Textarea
                rows={4}
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder={t("fraud_report.reason_placeholder")}
                data-testid="report-reason-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">{t("fraud_report.attached_note")}</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setReportOpen(false)} disabled={reporting} data-testid="report-cancel">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={submitReport}
              disabled={reporting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="report-submit"
            >
              {reporting ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />{t("fraud_report.submitting")}</>
              ) : (
                <><Flag className="w-4 h-4 mr-1" />{t("fraud_report.submit")}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
